import { useEffect, useRef, useState } from "react";
import { UserButton, useAuth } from "@clerk/clerk-react";
import { TerminalSquare, HardDrive, ShieldAlert } from "lucide-react";
import { getAdminStatus } from "@/services/adminService";

/**
 * Clerk's UserButton with Taitora's own menu entries appended.
 *
 * Rendered in two places — the expanded sidebar footer (desktop) and the
 * collapsed rail (mobile) — so the menu is defined here once rather than
 * duplicated at both call sites and drifting apart.
 *
 * Clerk keeps owning account management ("Manage account", "Sign out"); we only
 * add what is ours. `href` navigates through the router because ClerkProvider
 * is mounted inside BrowserRouter with routerPush wired up in main.tsx.
 *
 * The Administration entry appears only for platform admins. Hiding it here is
 * cosmetic — the backend 404s every /api/admin route for non-admins — so a
 * failed or slow status probe just leaves the item out.
 */
export default function TaitoraUserButton() {
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAdminStatus(getTokenRef.current)
      .then((status) => {
        if (!cancelled && status) setIsAdmin(true);
      })
      .catch(() => {
        /* not an admin as far as the menu is concerned */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <UserButton afterSignOutUrl="/">
      <UserButton.MenuItems>
        <UserButton.Link
          label="MCP access"
          labelIcon={<TerminalSquare size={16} />}
          href="/settings/mcp"
        />
        {/* Device-scoped, not account-scoped — which is why it sits here rather
            than as a custom page inside Clerk's account modal. */}
        <UserButton.Link
          label="Local storage"
          labelIcon={<HardDrive size={16} />}
          href="/settings/storage"
        />
        {isAdmin && (
          <UserButton.Link
            label="Administration"
            labelIcon={<ShieldAlert size={16} />}
            href="/settings/admin"
          />
        )}
      </UserButton.MenuItems>
    </UserButton>
  );
}
